import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, CalendarRange, MessageSquareText, Users } from 'lucide-react'
import { GestorJornadaFeedbackAlert } from '@/components/Gestores/GestorJornadaFeedbackAlert'
import { GestorJornadaFilters } from '@/components/Gestores/GestorJornadaFilters'
import { GestorJornadaMembrosPanel } from '@/components/Gestores/GestorJornadaMembrosPanel'
import {
  type JornadaSituacaoFilter,
  type JornadaViewMode,
} from '@/components/Gestores/gestorJornadaFilters'
import {
  buildGestoresEquipeMemberUrl,
  buildGestoresJornadaReturnQuery,
} from '@/components/Gestores/gestoresJornadaNavigation'
import { toast } from 'sonner'
import { useGestorJornadaTab } from '@/hooks/useGestorJornadaTab'

function KpiCard({
  label,
  value,
  icon,
  tone,
}: {
  label: string
  value: number
  icon: React.ReactNode
  tone?: 'danger' | 'warn' | 'neutral'
}) {
  const toneClass =
    tone === 'danger'
      ? 'border-red-500/25 bg-red-500/[0.06]'
      : tone === 'warn'
        ? 'border-amber-500/25 bg-amber-500/[0.06]'
        : 'border-white/[0.06] bg-white/[0.03]'

  return (
    <div className={`rounded-xl border px-3 py-2.5 sm:p-4 ${toneClass}`}>
      <div className='flex items-center gap-2 text-text-muted'>
        {icon}
        <span className='text-[10px] font-semibold uppercase tracking-wide sm:text-xs'>{label}</span>
      </div>
      <p className='mt-1 text-xl font-bold tabular-nums text-text-primary sm:mt-2 sm:text-2xl'>
        {value}
      </p>
    </div>
  )
}

type Props = {
  reloadKey?: number
  search: string
  setSearch: (v: string) => void
}

export function GestorJornadaTab({ reloadKey = 0, search, setSearch }: Props) {
  const navigate = useNavigate()
  const jornada = useGestorJornadaTab()
  const back = buildGestoresJornadaReturnQuery()
  const [situacao, setSituacao] = useState<JornadaSituacaoFilter>('todas')
  const [view, setView] = useState<JornadaViewMode>('lista')

  const openFeedback = (memberId: string) => {
    navigate(buildGestoresEquipeMemberUrl(memberId, { section: 'feedback', back }))
  }

  useEffect(() => {
    if (reloadKey > 0) void jornada.reload()
  }, [reloadKey, jornada.reload])

  if (jornada.loading) {
    return (
      <div className='glass-card flex justify-center py-16'>
        <div className='h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent' />
      </div>
    )
  }

  if (jornada.error || !jornada.data) {
    return (
      <div className='rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger'>
        {jornada.error ?? 'Erro ao carregar jornada.'}
      </div>
    )
  }

  const { resumo, feedbackPendente, membros } = jornada.data
  const totalMembros = membros.length

  return (
      <div className='space-y-3'>
      <div className='grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4'>
        <KpiCard label='Profissionais' value={resumo.totalMembros} icon={<Users size={15} />} />
        <KpiCard
          label='1:1 atrasadas'
          value={resumo.atrasadas}
          icon={<AlertTriangle size={15} />}
          tone={resumo.atrasadas > 0 ? 'danger' : 'neutral'}
        />
        <KpiCard
          label='Próximas 7 dias'
          value={resumo.proximas7d}
          icon={<CalendarRange size={15} />}
          tone={resumo.proximas7d > 0 ? 'warn' : 'neutral'}
        />
        <KpiCard
          label='Feedbacks rascunho'
          value={resumo.feedbackRascunhos}
          icon={<MessageSquareText size={15} />}
          tone={resumo.feedbackRascunhos > 0 ? 'warn' : 'neutral'}
        />
      </div>

      <GestorJornadaFeedbackAlert
        feedbackPendente={feedbackPendente}
        onOpenFeedback={openFeedback}
      />

      <div className='space-y-3'>
        <GestorJornadaFilters
          membros={membros}
          search={search}
          setSearch={setSearch}
          situacao={situacao}
          setSituacao={setSituacao}
          view={view}
          setView={setView}
        />

        <p className='px-1 text-xs text-text-muted'>
          Exibindo profissionais conforme filtros · {totalMembros} no total da equipe
        </p>

        <GestorJornadaMembrosPanel
          membros={membros}
          search={search}
          situacao={situacao}
          view={view}
          registrandoId={jornada.registrandoId}
          onOpenFeedback={openFeedback}
          onCadenciaChange={async (pjId, cadencia) => {
            try {
              await jornada.updateCadencia(pjId, cadencia)
            } catch {
              toast.error('Falha ao atualizar cadência')
            }
          }}
          onRegister={async (pjId, body) => {
            try {
              await jornada.registerOneOnOne(pjId, body)
            } catch {
              toast.error('Falha ao registrar 1:1')
              throw new Error('register-failed')
            }
          }}
        />
      </div>
    </div>
  )
}
